import { useEffect } from "preact/hooks";
import type { RefObject } from "preact";

/**
 * Keeping the keyboard inside a modal layer (the sheets, the photo check, the tour): Tab and
 * Shift+Tab cycle through the layer's own controls, and everything outside it is `inert` while it is
 * open, so neither Tab nor a screen reader's cursor reaches the board behind. Traps stack in the
 * order they opened and only the top one answers, as the Escape layers do (src/escape.ts).
 */

/** What Tab can land on. Disabled controls, tabindex -1 and anything hidden or inert are filtered out after. */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * Where Tab goes inside a trap of `count` controls when the focus sits on control `index` (-1: on
 * the layer itself, or outside it). Returns the control to focus, -1 to hold the focus on the layer
 * (it has no controls), or null to let the browser move it (a step that stays inside).
 */
export function tabTarget(count: number, index: number, shift: boolean): number | null {
  if (count <= 0) return -1;
  if (index < 0 || index >= count) return shift ? count - 1 : 0;
  if (shift && index === 0) return count - 1;
  if (!shift && index === count - 1) return 0;
  return null;
}

/** The controls Tab can reach inside `root`, in document order. */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.tabIndex >= 0 && !el.closest("[inert]") && el.closest("[aria-hidden='true']") === null && el.getClientRects().length > 0,
  );
}

const traps: HTMLElement[] = [];
let listening = false;

function onKey(e: KeyboardEvent): void {
  if (e.key !== "Tab" || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
  const root = traps[traps.length - 1];
  if (!root || !root.isConnected) return;
  const list = focusablesIn(root);
  const active = document.activeElement;
  const index = active instanceof HTMLElement ? list.indexOf(active) : -1;
  const target = tabTarget(list.length, index, e.shiftKey);
  if (target === null) return;
  e.preventDefault();
  (target < 0 ? root : list[target]).focus();
}

/**
 * Makes every element outside `el` inert: at each level from `el` up to the body, its siblings.
 * Siblings already inert, or aria-hidden decoration (a sheet's "Tap to close" hint, which a tap
 * must still reach), are left as they are. Returns the undo, which touches only what it set.
 */
export function inertOutside(el: HTMLElement): () => void {
  const set: Element[] = [];
  let node: Element = el;
  while (node.parentElement && node !== document.body) {
    for (const sib of Array.from(node.parentElement.children)) {
      if (sib === node || sib.hasAttribute("inert") || sib.getAttribute("aria-hidden") === "true") continue;
      if (sib.tagName === "SCRIPT" || sib.tagName === "STYLE" || sib.tagName === "LINK") continue;
      sib.setAttribute("inert", "");
      set.push(sib);
    }
    node = node.parentElement;
  }
  return () => {
    for (const sib of set) sib.removeAttribute("inert");
  };
}

/**
 * Traps Tab inside `el` and makes the rest of the page inert until the returned function runs.
 * For hooks that already manage a layer's focus (src/devsheet-hooks.ts useSheetLayer), so they can
 * release the trap before handing the focus back to the opener (an inert opener cannot take it).
 */
export function trapFocus(el: HTMLElement): () => void {
  traps.push(el);
  const restore = inertOutside(el);
  if (!listening && typeof document !== "undefined") {
    document.addEventListener("keydown", onKey);
    listening = true;
  }
  return () => {
    restore();
    const i = traps.lastIndexOf(el);
    if (i >= 0) traps.splice(i, 1);
    if (traps.length === 0 && listening) {
      document.removeEventListener("keydown", onKey);
      listening = false;
    }
  };
}

/**
 * A modal layer that does not use useSheetLayer (the tour): traps focus in `ref`'s element while
 * `active`, and returns the focus to whatever had it when the trap closes.
 */
export function useFocusTrap(ref: RefObject<HTMLElement>, active: boolean): void {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const release = trapFocus(el);
    return () => {
      release();
      if (before && before.isConnected && before !== document.body) before.focus({ preventScroll: true });
    };
  }, [active]);
}
